import { INetmap, NodeType, NodeStatus, NetmapPosition } from "../types";
import * as Programs from "../programs";
import * as Dialogue from "./dialogues";
import * as NodeStyle from "./nodeStyle";
import Descriptions from "./nodeDescriptions";
import { resolveImage } from "../util/util";
import {
  revealNode,
  addProgram,
  setLevel,
  startDialogue,
  displayMessage,
  setNightfallNodes,
  chain,
  addCredits,
} from "../util/gscHelpers";

const { Battle, WarezNode, SmartHQ } = NodeType;

const Nodes = {
  S1: "smart-hq",
  W1: "warez-1",
  L1: "lmm-techsupport",
  F1: "ph-prdatabase",
  F2: "ph-employee",
  C1: "car-memorytower",
  F3: "ph-govaffairs",
  C2: "car-sydney",
  L2: "lmm-clubcenter",
  C3: "car-inventory",
  W2: "warez-2",
  L3: "lmm-eastern",
  F4: "ph-clinical",
  C4: "car-commshub",
  L4: "lmm-print",
  L5: "lmm-banane",
  D1: "donut-supply",
  D2: "donut-franchise",
  L6: "lmm-toy",
  W3: "warez-3",
  P1: "ped-offshore",
  F5: "ph-vaccine",
  F6: "ph-hmo",
  D3: "donut-market",
  D4: "donut-beverage",
  D5: "donut-flavor",
  L8: "lmm-jungle",
  P2: "ped-fiduciary",
  C5: "car-substation",
  C6: "car-archives",
  P3: "ped-reinsurance",
  L7: "lmm-assimilation",
  W4: "warez-4",
  D6: "donut-recipe",
  L9: "lmm-film",
  P4: "ped-rdbackup",
  F7: "ph-syscore",
  P5: "ped-executive",
  P6: "ped-treasury",
  P7: "ped-privileged",
  F8: "ph-proprietary",
  Q1: "disarray-hq",
};

const positions: Record<string, NetmapPosition> = {
  [Nodes.S1]: [197, 539],
  [Nodes.W1]: [104, 349],
  [Nodes.L1]: [100, 681],
  [Nodes.F1]: [343, 538],
  [Nodes.F2]: [437, 681],
  [Nodes.C1]: [386, 443],
  [Nodes.F3]: [483, 324],
  [Nodes.L2]: [340, 658],
  [Nodes.C2]: [241, 274],
  [Nodes.C3]: [242, 851],
  [Nodes.W2]: [291, 755],
  [Nodes.L3]: [340, 322],
  [Nodes.F4]: [385, 227],
  [Nodes.C4]: [532, 753],
  [Nodes.L4]: [388, 923],
  [Nodes.L5]: [150, 950],
  [Nodes.D1]: [628, 635],
  [Nodes.D2]: [821, 538],
  [Nodes.L6]: [483, 105],
  [Nodes.W3]: [675, 80],
  [Nodes.P1]: [627, 297],
  [Nodes.F5]: [725, 439],
  [Nodes.F6]: [484, 465],
  [Nodes.D3]: [963, 705],
  [Nodes.D4]: [869, 897],
  [Nodes.D5]: [722, 732],
  [Nodes.L8]: [1203, 732],
  [Nodes.P2]: [1058, 587],
  [Nodes.C5]: [965, 441],
  [Nodes.C6]: [916, 347],
  [Nodes.P3]: [1298, 610],
  [Nodes.L7]: [1106, 873],
  [Nodes.W4]: [1299, 971],
  [Nodes.D6]: [1443, 705],
  [Nodes.L9]: [1343, 898],
  [Nodes.P4]: [1538, 587],
  [Nodes.F7]: [1348, 490],
  [Nodes.P5]: [1206, 321],
  [Nodes.P6]: [1060, 130],
  [Nodes.P7]: [987, 310],
  [Nodes.F8]: [868, 225],
  [Nodes.Q1]: [1444, 299],
};

const netmap: INetmap = {
  dimensions: [1958, 1412],
  image: resolveImage("bg/netmap.png"),
  initialSave: {
    firstClearNode: null,
    scrollPosition: [0, 260],
    availablePrograms: [Programs.Hack, Programs.Slingshot],
    numCredits: 0,
    netmapStatus: {
      [Nodes.S1]: NodeStatus.CLEARED,
      [Nodes.W1]: NodeStatus.UNCLEARED_UNATTEMPTED,
      [Nodes.F1]: NodeStatus.UNCLEARED_UNATTEMPTED,
      [Nodes.L1]: NodeStatus.UNCLEARED_UNATTEMPTED,
    },
    collectedCreditIDs: [],
    securityLevel: 1,
    completedTutorial: false,
  },
  positions,
  nodes: [
    {
      ...NodeStyle.smart,
      id: Nodes.S1,
      name: "S.M.A.R.T HQ",
      description: Descriptions.S1,
      securityLevel: 1,
      type: SmartHQ,
    },

    // LEVEL 1

    {
      ...NodeStyle.warez,
      id: Nodes.W1,
      name: "Leo's Shop",
      description: Descriptions.W1,
      securityLevel: 1,
      type: WarezNode,
      preOpen: (gsc) => {
        if (gsc.getNodeStatus(Nodes.W1) === NodeStatus.CLEARED) {
          return gsc.startDialogue(Dialogue.LeoRevisit);
        } else {
          return gsc.startDialogue(Dialogue.LeoInitial);
        }
      },
    },
    {
      ...NodeStyle.lmm,
      id: Nodes.L1,
      description: Descriptions.L1,
      name: "Tech Support",
      securityLevel: 1,
      type: Battle,
      firstClearCredits: 400,
      onFirstClear: startDialogue(Dialogue.DisarrayL1),
    },
    {
      ...NodeStyle.ph,
      id: Nodes.F1,
      description: Descriptions.F1,
      name: "PR Database",
      securityLevel: 1,
      type: Battle,
      firstClearCredits: 400,
      onFirstClear: chain(startDialogue(Dialogue.SpinnerF1), revealNode(Nodes.F3)),
    },
    {
      ...NodeStyle.ph,
      id: Nodes.F2,
      description: Descriptions.F2,
      name: "Employee Records",
      securityLevel: 1,
      type: Battle,
      prereq: Nodes.F1,
      firstClearCredits: 400,
      onFirstClear: startDialogue(Dialogue.SuperphreakF2),
    },
    {
      ...NodeStyle.car,
      id: Nodes.C1,
      description: Descriptions.C1,
      name: "Memory Tower",
      securityLevel: 1,
      type: Battle,
      prereq: Nodes.F1,
      firstClearCredits: 400,
      onFirstClear: startDialogue(Dialogue.SuperphreakC1),
    },
    {
      ...NodeStyle.ph,
      id: Nodes.F3,
      description: Descriptions.F3,
      name: "Government Affairs",
      securityLevel: 1,
      type: Battle,
      prereq: Nodes.C1,
      firstClearCredits: 400,
      onFirstClear: chain(startDialogue(Dialogue.SpinnerF3), addProgram(Programs.Clog)),
    },
    {
      ...NodeStyle.car,
      id: Nodes.C2,
      description: Descriptions.C2,
      name: "Sydney Project",
      securityLevel: 1,
      type: Battle,
      prereq: Nodes.C1,
      firstClearCredits: 400,
      onFirstClear: chain(startDialogue(Dialogue.SuperphreakC2), setLevel(2), revealNode(Nodes.W2)),
    },

    // LEVEL 2

    {
      ...NodeStyle.lmm,
      id: Nodes.L2,
      description: Descriptions.L2,
      name: "Club Center",
      securityLevel: 2,
      type: Battle,
      prereq: Nodes.L1,
      firstClearCredits: 600,
      onFirstClear: chain(startDialogue(Dialogue.WintermutantL3), revealNode(Nodes.D1)),
    },
    {
      ...NodeStyle.car,
      id: Nodes.C3,
      description: Descriptions.C3,
      name: "Inventory Archives",
      securityLevel: 2,
      type: Battle,
      prereq: Nodes.L2,
      firstClearCredits: 600,
      onFirstClear: startDialogue(Dialogue.SuperphreakC3),
    },
    {
      ...NodeStyle.warez,
      id: Nodes.W2,
      name: "Network City",
      description: Descriptions.W2,
      securityLevel: 2,
      type: WarezNode,
      prereq: Nodes.C3,
      firstClearCredits: 600,
      preOpen: (gsc) => {
        if (gsc.getNodeStatus(Nodes.W2) === NodeStatus.CLEARED) {
          return gsc.startDialogue(Dialogue.TsumaraRevisit);
        } else {
          return gsc.startDialogue(Dialogue.TsumaraInitial);
        }
      },
    },
    {
      ...NodeStyle.lmm,
      id: Nodes.L3,
      description: Descriptions.L3,
      name: "Eastern Distribution Site",
      securityLevel: 2,
      type: Battle,
      prereq: Nodes.C1,
      firstClearCredits: 600,
      onFirstClear: startDialogue(Dialogue.SuperphreakL3),
    },
    {
      ...NodeStyle.ph,
      id: Nodes.F4,
      description: Descriptions.F4,
      name: "Clinical Trial Database",
      securityLevel: 2,
      type: Battle,
      prereq: Nodes.L3,
      firstClearCredits: 600,
      onFirstClear: startDialogue(Dialogue.DisarrayF4),
    },
    {
      ...NodeStyle.car,
      id: Nodes.C4,
      description: Descriptions.C4,
      name: "Communications Hub",
      securityLevel: 2,
      type: Battle,
      prereq: Nodes.L2,
      firstClearCredits: 600,
      onFirstClear: chain(startDialogue(Dialogue.JoanaC4), revealNode(Nodes.C6)),
    },
    {
      ...NodeStyle.lmm,
      id: Nodes.L4,
      description: Descriptions.L4,
      name: "Print Assets",
      securityLevel: 2,
      type: Battle,
      prereq: Nodes.C4,
      firstClearCredits: 600,
      onFirstClear: startDialogue(Dialogue.WintermutantL4),
    },
    {
      ...NodeStyle.lmm,
      id: Nodes.L5,
      description: Descriptions.L5,
      name: "Banane System",
      securityLevel: 2,
      type: Battle,
      prereq: Nodes.L4,
      firstClearCredits: 600,
      onFirstClear: chain(startDialogue(Dialogue.SuperphreakL5), setLevel(3), revealNode(Nodes.W3)),
    },
    {
      ...NodeStyle.donut,
      id: Nodes.D1,
      description: Descriptions.D1,
      name: "Supply Management",
      securityLevel: 2,
      type: Battle,
      prereq: Nodes.C4,
      firstClearCredits: 600,
      onFirstClear: chain(startDialogue(Dialogue.DisarrayD1), revealNode(Nodes.D3)),
    },
    {
      ...NodeStyle.donut,
      id: Nodes.D2,
      description: Descriptions.D2,
      name: "Franchise Office",
      securityLevel: 2,
      type: Battle,
      prereq: Nodes.D1,
      firstClearCredits: 600,
      onFirstClear: startDialogue(Dialogue.SuperphreakD2),
    },

    // LEVEL 3

    {
      ...NodeStyle.lmm,
      id: Nodes.L6,
      description: Descriptions.L6,
      name: "Toy Properties",
      securityLevel: 3,
      type: Battle,
      prereq: Nodes.F4,
      firstClearCredits: 800,
      onFirstClear: chain(
        startDialogue(Dialogue.SuperphreakL6),
        displayMessage("transmission", "Logs Sent")
      ),
    },
    {
      ...NodeStyle.warez,
      id: Nodes.W3,
      name: "Ready to Ware",
      description: Descriptions.W3,
      securityLevel: 3,
      type: WarezNode,
      prereq: Nodes.L6,
      firstClearCredits: 800,
      preOpen: (gsc) => {
        if (gsc.getNodeStatus(Nodes.W3) === NodeStatus.CLEARED) {
          return gsc.startDialogue(Dialogue.KimoniRevisit);
        } else {
          return gsc.startDialogue(Dialogue.KimoniInitial);
        }
      },
    },
    {
      ...NodeStyle.ped,
      id: Nodes.P1,
      description: Descriptions.P1,
      name: "Off-Shore Transactions",
      securityLevel: 3,
      type: Battle,
      prereq: Nodes.L6,
      firstClearCredits: 800,
      onFirstClear: startDialogue(Dialogue.DisarrayP1),
    },
    {
      ...NodeStyle.ph,
      id: Nodes.F5,
      description: Descriptions.F5,
      name: "Vaccine Database",
      securityLevel: 3,
      type: Battle,
      prereq: Nodes.P1,
      firstClearCredits: 800,
      onFirstClear: chain(startDialogue(Dialogue.SuperphreakF5), revealNode(Nodes.Q1)),
    },
    {
      ...NodeStyle.ph,
      id: Nodes.F6,
      description: Descriptions.F6,
      name: "HMO Procedure Management",
      securityLevel: 3,
      type: Battle,
      prereq: Nodes.F5,
      firstClearCredits: 800,
      onFirstClear: chain(startDialogue(Dialogue.SuperphreakF6), setLevel(4), revealNode(Nodes.W4)),
    },
    {
      ...NodeStyle.donut,
      id: Nodes.D3,
      description: Descriptions.D3,
      name: "Market Research",
      securityLevel: 3,
      type: Battle,
      prereq: Nodes.D2,
      firstClearCredits: 800,
      onFirstClear: chain(startDialogue(Dialogue.WintermutantD3), revealNode(Nodes.L9)),
    },
    {
      ...NodeStyle.donut,
      id: Nodes.D4,
      description: Descriptions.D4,
      name: "Beverage Subsidiaries",
      securityLevel: 3,
      type: Battle,
      prereq: Nodes.D3,
      firstClearCredits: 800,
      onFirstClear: chain(startDialogue(Dialogue.SpinnerD4), revealNode(Nodes.P7)),
    },
    {
      ...NodeStyle.donut,
      id: Nodes.D5,
      description: Descriptions.D5,
      name: "Flavor Evaluation Lab",
      securityLevel: 3,
      type: Battle,
      prereq: Nodes.D3,
      firstClearCredits: 800,
      onFirstClear: startDialogue(Dialogue.SuperphreakD5),
    },
    {
      ...NodeStyle.lmm,
      id: Nodes.L8,
      description: Descriptions.L8,
      name: "Lucky Jungle Central",
      securityLevel: 3,
      type: Battle,
      prereq: Nodes.D3,
      firstClearCredits: 800,
      onFirstClear: startDialogue(Dialogue.DisarrayL8),
    },
    {
      ...NodeStyle.ped,
      id: Nodes.P2,
      description: Descriptions.P2,
      name: "Fiduciary Node",
      securityLevel: 3,
      type: Battle,
      prereq: Nodes.D2,
      onFirstClear: startDialogue(Dialogue.SpinnerP2),
    },
    {
      ...NodeStyle.car,
      id: Nodes.C5,
      description: Descriptions.C5,
      name: "Sub-Station Gamma",
      securityLevel: 3,
      type: Battle,
      prereq: Nodes.P2,
      firstClearCredits: 800,
      onFirstClear: startDialogue(Dialogue.JoanaC5),
    },
    {
      ...NodeStyle.car,
      id: Nodes.C6,
      description: Descriptions.C6,
      name: "S.A. Archives",
      securityLevel: 3,
      type: Battle,
      prereq: Nodes.C5,
      firstClearCredits: 800,
      onFirstClear: chain(startDialogue(Dialogue.JoanaC6), addProgram(Programs.MemoryHog)),
    },
    {
      ...NodeStyle.ped,
      id: Nodes.P3,
      description: Descriptions.P3,
      name: "Re-Insurance Database",
      securityLevel: 3,
      type: Battle,
      prereq: Nodes.P2,
      firstClearCredits: 800,
      onFirstClear: chain(startDialogue(Dialogue.SpinnerP3), addCredits(500)),
    },

    // LEVEL 4

    {
      ...NodeStyle.lmm,
      id: Nodes.L7,
      description: Descriptions.L7,
      name: "Assimilation Timetable",
      securityLevel: 4,
      type: Battle,
      prereq: Nodes.D4,
      firstClearCredits: 1000,
      onFirstClear: chain(startDialogue(Dialogue.WintermutantL7), revealNode(Nodes.F8)),
    },
    {
      ...NodeStyle.warez,
      id: Nodes.W4,
      name: "Agora",
      description: Descriptions.W4,
      securityLevel: 4,
      type: WarezNode,
      prereq: Nodes.L7,
      firstClearCredits: 1000,
      preOpen: (gsc) => {
        if (gsc.getNodeStatus(Nodes.W4) === NodeStatus.CLEARED) {
          return gsc.startDialogue(Dialogue.MinervaRevisit);
        } else {
          return gsc.startDialogue(Dialogue.MinervaInitial);
        }
      },
    },
    {
      ...NodeStyle.donut,
      id: Nodes.D6,
      description: Descriptions.D6,
      name: "Recipe Database",
      securityLevel: 4,
      type: Battle,
      prereq: Nodes.L7,
      firstClearCredits: 1000,
      onFirstClear: startDialogue(Dialogue.SuperphreakD6),
    },
    {
      ...NodeStyle.lmm,
      id: Nodes.L9,
      description: Descriptions.L9,
      name: "Film Properties",
      securityLevel: 4,
      type: Battle,
      prereq: Nodes.D6,
      firstClearCredits: 1000,
      onFirstClear: chain(startDialogue(Dialogue.WintermutantL9), addProgram(Programs.Wizard)),
    },
    {
      ...NodeStyle.ped,
      id: Nodes.P4,
      description: Descriptions.P4,
      name: "R&D Backup",
      securityLevel: 4,
      type: Battle,
      prereq: Nodes.D6,
      firstClearCredits: 1000,
      onFirstClear: startDialogue(Dialogue.JoanaP4),
    },
    {
      ...NodeStyle.ph,
      id: Nodes.F7,
      description: Descriptions.F7,
      name: "System Core",
      securityLevel: 4,
      type: Battle,
      prereq: Nodes.P4,
      firstClearCredits: 1000,
      onFirstClear: startDialogue(Dialogue.JoanaF7),
    },
    {
      ...NodeStyle.ped,
      id: Nodes.P5,
      description: Descriptions.P5,
      name: "Executive Protocol",
      securityLevel: 4,
      type: Battle,
      prereq: Nodes.F7,
      firstClearCredits: 1000,
      onFirstClear: startDialogue(Dialogue.DisarrayP5),
    },
    {
      ...NodeStyle.ped,
      id: Nodes.P6,
      description: Descriptions.P6,
      name: "Treasury Funds",
      securityLevel: 4,
      type: Battle,
      prereq: Nodes.P5,
      firstClearCredits: 1000,
      onFirstClear: startDialogue(Dialogue.SuperphreakP6),
    },
    {
      ...NodeStyle.ped,
      id: Nodes.P7,
      description: Descriptions.P7,
      name: "Privileged Accounts",
      securityLevel: 4,
      type: Battle,
      prereq: Nodes.P6,
      firstClearCredits: 1000,
      onFirstClear: chain(startDialogue(Dialogue.SpinnerP7), addCredits(2000)),
    },
    {
      ...NodeStyle.ph,
      id: Nodes.F8,
      description: Descriptions.F8,
      name: "Proprietary Research",
      securityLevel: 4,
      type: Battle,
      prereq: Nodes.P6,
      firstClearCredits: 1000,
      onFirstClear: chain(
        startDialogue(Dialogue.DisarrayF8),
        setLevel(5),
        revealNode(Nodes.Q1),
        setNightfallNodes([Nodes.Q1])
      ),
    },

    // LEVEL 5

    {
      ...NodeStyle.hq,
      id: Nodes.Q1,
      description: Descriptions.Q1,
      name: "Unknown Node",
      securityLevel: 5,
      type: Battle,
      prereq: Nodes.F7,
      firstClearCredits: 1000,
      onFirstLose: chain(startDialogue(Dialogue.SuperphreakQ1Lose), setNightfallNodes()),
      onFirstClear: chain(setNightfallNodes(), startDialogue(Dialogue.SuperphreakQ1Win)),
    },
  ],
};

export default netmap;
